import { listHerdsByOrganizationQueryOptions } from "@packages/api-client/hooks/livestockController";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
	ImportHistory,
	useImportHistory,
} from "@/components/import/ImportHistory";
import { Eyebrow } from "@/components/import/ImportPrimitives";
import { AnimalImportWizard } from "@/components/livestock/import/AnimalImportWizard";
import { requireReachableRoute } from "@/lib/offline/routeGuard";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/livestock/$establishmentId/animals/import",
)({
	// Desk work end to end: a file upload, an AI extraction and a bulk write. None
	// of it degrades usefully without a connection, and half an import is worse
	// than none — so this is online-only by design, not by omission.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	// `?import=<mediaFileId>` is the ADDRESS of an import, and the wizard resolves
	// it against the server rather than against anything this tab did earlier.
	// That is what makes the page reload-safe, bookmarkable and shareable within
	// the farm — and it is why the id is only ever a hint here: the lifecycle read
	// 404s an id from another org or another farm, so a typed one buys nothing.
	validateSearch: (search: Record<string, unknown>): { import?: string } => ({
		import:
			typeof search.import === "string" && search.import
				? search.import
				: undefined,
	}),
	loader: ({ context, params }) => {
		// The mapping panel needs the farm's existing lots the moment review
		// renders, and the extraction ahead of it takes minutes — so this is warm
		// long before it is read.
		context.queryClient.ensureQueryData(
			listHerdsByOrganizationQueryOptions(params.orgId, {
				establishmentId: params.establishmentId,
			}),
		);
	},
	component: AnimalImportPage,
});

function AnimalImportPage() {
	const { t } = useTranslation();
	// The farm is intrinsic to an import — every animal it creates belongs to one
	// establishment — so it rides in the PATH, not in `?farm=`.
	const { orgId, establishmentId } = Route.useParams();
	const { import: importId } = Route.useSearch();
	const navigate = useNavigate();
	const history = useImportHistory(orgId, "animals", establishmentId);

	const { data: herdsData } = useQuery(
		listHerdsByOrganizationQueryOptions(orgId, { establishmentId }),
	);
	const herds = (herdsData?.data ?? [])
		.filter((herd) => herd.isActive)
		.map((herd) => ({
			id: herd.id,
			name: herd.name,
		}));

	// Both exits land on the animals tab with this farm selected. `farm` is
	// carried explicitly: the section index reads its scope from the search param,
	// and dropping it here would desync path and param on the way back.
	const goToAnimals = () =>
		navigate({
			to: "/app/org/$orgId/livestock",
			params: { orgId },
			search: { tab: "animals", farm: establishmentId },
		});

	return (
		<div className="mx-auto flex w-full max-w-[1080px] flex-col gap-4 md:gap-6">
			<div className="flex flex-col gap-1 md:gap-1.5">
				<Eyebrow>{t("livestock.animalImport.eyebrow")}</Eyebrow>
				<h1
					className="font-display text-[clamp(24px,4vw,40px)] font-extrabold leading-[1.1] tracking-[-0.02em] text-base-content"
					data-testid="page-title"
				>
					{t("livestock.animalImport.pageTitle")}
				</h1>
				<p className="max-w-[56ch] text-sm leading-relaxed text-base-content/70 md:text-[15px]">
					{t("livestock.animalImport.pageSubtitle")}
				</p>
			</div>
			<AnimalImportWizard
				orgId={orgId}
				establishmentId={establishmentId}
				herds={herds}
				importId={importId}
				// `replace` so the browser Back button leaves the import page instead
				// of walking the producer back through every step they just passed.
				onImportChange={(mediaFileId) =>
					navigate({
						to: ".",
						search: (prev) => ({
							...prev,
							import: mediaFileId ?? undefined,
						}),
						replace: true,
					})
				}
				onDone={goToAnimals}
				onManualEntry={() =>
					navigate({
						to: "/app/org/$orgId/livestock/$establishmentId/animals/new",
						params: { orgId, establishmentId },
					})
				}
			/>
			<ImportHistory
				kind="animals"
				items={history.data?.data ?? []}
				isLoading={history.isPending}
				isError={history.isError}
				hasMore={history.hasNextPage}
				isLoadingMore={history.isFetchingNextPage}
				onLoadMore={() => void history.fetchNextPage()}
				onRetry={() => {
					void history.refetch();
				}}
				onOpen={(item) => {
					void navigate({
						to: ".",
						search: (prev) => ({ ...prev, import: item.id }),
					});
				}}
				onRecordOpen={(animalId) => {
					void navigate({
						to: "/app/org/$orgId/livestock/$establishmentId/animals/$animalId",
						params: { orgId, establishmentId, animalId },
					});
				}}
			/>
		</div>
	);
}
